import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { draftKey, saveDraft, loadDraft, clearDraft } from "@/lib/cleaningDraft";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Camera,
  Upload,
  CheckCircle2,
  XCircle,
  ImagePlus,
  AlertTriangle,
  Sparkles,
  Trash2,
  X as XIcon,
  Hourglass,
  ShieldCheck,
  PlusCircle,
  Clock,
  AlertCircle,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { CameraCapture } from "@/components/CameraCapture";
import { ImageLightbox } from "@/components/ImageLightbox";
import { fmtDate, getCurrentLocation, todayISO, errorMsg } from "@/lib/utils-app";
import { useGpsTrail } from "@/lib/useGpsTrail";

const ASSET_TYPE_LABEL = {
  road: "Road",
  osr: "OSR",
  footpath: "Footpath",
  sipcot_po_office: "SIPCOT PO office",
  dumping_yard: "Dumping yard",
  other: "Other",
};

const stampGallery = async (file) => {
  // Read a gallery-uploaded image and burn an IST date+time stamp at the
  // bottom so it's consistent with live-camera captures.
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  // Cap large images to 1280 max-side
  const max = 1280;
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const tfmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const now = new Date();
  const stamp = `${fmt.format(now)}  ${tfmt.format(now)}  IST`;
  const fontSize = Math.max(18, Math.round(h * 0.045));
  const padY = Math.round(fontSize * 0.45);
  ctx.font = `700 ${fontSize}px Arial, sans-serif`;
  ctx.textBaseline = "alphabetic";
  const boxH = fontSize + padY * 2;
  ctx.fillStyle = "rgba(0,0,0,0.55)";
  ctx.fillRect(0, h - boxH, w, boxH);
  ctx.shadowColor = "rgba(0,0,0,0.9)";
  ctx.shadowBlur = 2;
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.fillText(stamp, w / 2, h - padY);
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.textAlign = "start";
  return cv.toDataURL("image/jpeg", 0.85);
};

function PhotoStrip({ photos, onRemove, onZoom }) {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {photos.map((p, i) => (
        <div key={i} className="relative h-24 w-24 overflow-hidden rounded-md border bg-slate-900/90">
          <button type="button" onClick={() => onZoom(p.photo_b64)} className="absolute inset-0">
            <img src={p.photo_b64} alt="" className="h-full w-full object-contain" />
          </button>
          {onRemove ? (
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="absolute right-1 top-1 rounded-full bg-white/90 p-0.5 text-rose-600 shadow"
              aria-label="Remove"
              data-testid={`pmc-clean-photo-remove-${i}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function YesNoToggle({ value, onChange, testid }) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        size="sm"
        variant={value === true ? "default" : "outline"}
        onClick={() => onChange(true)}
        data-testid={`${testid}-yes`}
        className={value === true ? "bg-emerald-600 hover:bg-emerald-600 text-white" : ""}
      >
        <CheckCircle2 className="mr-1 h-4 w-4" /> Yes
      </Button>
      <Button
        type="button"
        size="sm"
        variant={value === false ? "default" : "outline"}
        onClick={() => onChange(false)}
        data-testid={`${testid}-no`}
        className={value === false ? "bg-rose-600 hover:bg-rose-600 text-white" : ""}
      >
        <XCircle className="mr-1 h-4 w-4" /> No
      </Button>
    </div>
  );
}

function CustomQuestionsRenderer({ kind, questions, answers, setAnswer }) {
  const list = (questions || []).filter((q) => q.active !== false);
  if (!list.length) return null;
  return (
    <div className="space-y-3" data-testid={`pmc-clean-custom-${kind}`}>
      {list.map((q) => {
        const ans = answers?.[q.id] || {};
        const value = ans.value;
        const requiresReason =
          q.question_type === "yes_no"
          && q.reason_when_value
          && String(value || "").toLowerCase() === String(q.reason_when_value).toLowerCase();
        return (
          <div key={q.id} className="rounded-md border bg-slate-50/50 p-3" data-testid={`pmc-clean-cq-${q.id}`}>
            <Label className="text-xs">
              {q.label}{q.required ? " *" : ""}
            </Label>
            {q.help_text ? (
              <div className="mt-0.5 text-[11px] text-slate-500">{q.help_text}</div>
            ) : null}
            <div className="mt-1.5">
              {q.question_type === "yes_no" && (
                <YesNoToggle
                  value={value === "yes" ? true : value === "no" ? false : null}
                  onChange={(v) => setAnswer(q.id, { value: v ? "yes" : "no" })}
                  testid={`pmc-clean-cq-${q.id}`}
                />
              )}
              {q.question_type === "short_text" && (
                <Input
                  value={value ?? ""}
                  onChange={(e) => setAnswer(q.id, { value: e.target.value })}
                  data-testid={`pmc-clean-cq-${q.id}-input`}
                />
              )}
              {q.question_type === "number" && (
                <Input
                  type="number"
                  inputMode="decimal"
                  value={value ?? ""}
                  onChange={(e) => setAnswer(q.id, { value: e.target.value })}
                  data-testid={`pmc-clean-cq-${q.id}-input`}
                />
              )}
              {q.question_type === "multi_choice" && (
                <Select
                  value={value || "_unset"}
                  onValueChange={(v) => setAnswer(q.id, { value: v === "_unset" ? "" : v })}
                >
                  <SelectTrigger data-testid={`pmc-clean-cq-${q.id}-select`}>
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_unset">— Select —</SelectItem>
                    {(q.options || []).map((opt) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {requiresReason && (
              <Textarea
                rows={2}
                className="mt-2"
                placeholder={q.reason_label || "Please explain…"}
                value={ans.reason || ""}
                onChange={(e) => setAnswer(q.id, { reason: e.target.value })}
                data-testid={`pmc-clean-cq-${q.id}-reason`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function PmcCleaningPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const parkId = searchParams.get("park") || "";

  const [park, setPark] = useState(null);
  const [todayState, setTodayState] = useState({ by_park: {} });
  // Cross-park sequencing: requests this supervisor has made (today) to
  // start a new location before finishing a previous one's After-cleaning.
  const [myLocationRequests, setMyLocationRequests] = useState([]);
  const [locationRequestOpen, setLocationRequestOpen] = useState(false);
  const [locationRequestReason, setLocationRequestReason] = useState("");
  const [locationRequestBusy, setLocationRequestBusy] = useState(false);

  // Log GPS pings while supervisor is in the cleaning checklist so the trail
  // is populated even when no live attendance capture happens (gallery-upload
  // workflows etc.).
  useGpsTrail({ enabled: true, parkIdRef: parkId });
  const [assets, setAssets] = useState([]);
  const [todayPlan, setTodayPlan] = useState(null);
  // visitMode is now AUTO-DERIVED from the visit status the supervisor set on
  // the Today screen — no duplicate ask here. "visited" → live camera;
  // "remote" → gallery upload allowed.
  const [visitMode, setVisitMode] = useState("visited");
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // BEFORE form state
  const [beforeAssetType, setBeforeAssetType] = useState("road");
  const [beforeAssetId, setBeforeAssetId] = useState("");
  // Ticking clock used to live-update reset-window countdown buttons.
  const [nowTs, setNowTs] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  // Multi-asset selection (one combined Before+After record can now cover
  // many assets cleaned on the same day). Each entry stores both the asset
  // id and its denormalised asset_type so we can render the right unit suffix
  // (km vs acres) and per-asset length input later in the After step.
  const [beforeAssetEntries, setBeforeAssetEntries] = useState([]);
  // Per-asset length cleaned dict {asset_id: "km or acres value"} — captured
  // in the After step. Falls back to legacy `lengthCleaned` when only one
  // asset is selected (so the previous mobile UI keeps working).
  const [afterPerAssetLengths, setAfterPerAssetLengths] = useState({});
  const [uniformOk, setUniformOk] = useState(null);
  const [uniformRemark, setUniformRemark] = useState("");
  const [toolkitOk, setToolkitOk] = useState(null);
  const [toolkitRemark, setToolkitRemark] = useState("");
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [showBeforeCamera, setShowBeforeCamera] = useState(false);
  // Workers-at-work photos (mandatory 1..4 per Before-cleaning submission).
  // Captured live when visited, gallery upload when Not Visited / remote.
  // GPS coordinates are mandatory per backend validation.
  const [workerPhotos, setWorkerPhotos] = useState([]);
  const [showWorkerCamera, setShowWorkerCamera] = useState(false);
  const [notVisitedReason, setNotVisitedReason] = useState("");
  const [submittingBefore, setSubmittingBefore] = useState(false);

  // AFTER form state
  const [completionMode, setCompletionMode] = useState("");
  const [lengthCleaned, setLengthCleaned] = useState("");
  const [asPerPlan, setAsPerPlan] = useState(null);
  const [notCompletedAsPerPlanReason, setNotCompletedAsPerPlanReason] = useState("");
  const [afterRemarks, setAfterRemarks] = useState("");
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [showAfterCamera, setShowAfterCamera] = useState(false);
  const [submittingAfter, setSubmittingAfter] = useState(false);
  // Waste removal — mandatory question on every After-cleaning submission.
  const [wasteRemoved, setWasteRemoved] = useState(null);
  const [wasteNotRemovedReason, setWasteNotRemovedReason] = useState("");
  // True when this park has been marked "Not Visited" by the PMC on Today.
  // Cleaning checklists still use the full visited-mode UI; this only drives
  // an informational banner so the supervisor remembers the context. Also
  // forces the Before/After photo panels into gallery-upload mode (since the
  // supervisor wasn't physically on site).
  const [parkNotVisitedToday, setParkNotVisitedToday] = useState(false);

  // Single source of truth: whenever the park is Not Visited (or the cleaning
  // checklist itself is being filed remotely), photo capture switches to
  // device-gallery upload instead of live camera.
  const useGalleryForPhotos = parkNotVisitedToday || visitMode === "remote";

  // -------- Reset Before Approval Request State (when 10-min window expires) --------
  const [resetBeforeOpen, setResetBeforeOpen] = useState(false);
  const [resetBeforeRemarks, setResetBeforeRemarks] = useState("");
  const [submittingResetBefore, setSubmittingResetBefore] = useState(false);

  const submitResetBeforeRequest = async () => {
    const remarks = (resetBeforeRemarks || "").trim();
    if (!remarks) return toast.error("Remarks / business justification is required");
    setSubmittingResetBefore(true);
    try {
      const payload = {
        request_type: "reset_before",
        park_id: parkId,
        reason: remarks,
      };
      const { data } = await api.post("/pmc/cleaning-location-requests", payload);
      toast.success("Request for Admin approval to reset Before-cleaning submitted successfully");
      setMyLocationRequests((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
      setResetBeforeOpen(false);
      setResetBeforeRemarks("");
    } catch (err) {
      toast.error(errorMsg(err, "Failed to submit reset approval request"));
    } finally {
      setSubmittingResetBefore(false);
    }
  };

  // -------- Add New Work Location State (when workers move to a new location) --------
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [addAssetType, setAddAssetType] = useState("road");
  const [addAssetId, setAddAssetId] = useState("");
  const [addLocationRemarks, setAddLocationRemarks] = useState("");
  const [submittingAddLocation, setSubmittingAddLocation] = useState(false);

  const availableAddAssets = useMemo(() => {
    const todayParkData = (todayState?.by_park || {})[parkId] || {};
    const existingIds = new Set((todayParkData.before?.asset_entries || []).map((e) => e.asset_id));
    if (todayParkData.before?.asset_id) existingIds.add(todayParkData.before.asset_id);
    (myLocationRequests || []).forEach((r) => {
      if (r.park_id === parkId && r.asset_id && r.status === "pending") {
        existingIds.add(r.asset_id);
      }
    });
    return assets.filter((a) => a.asset_type === addAssetType && !existingIds.has(a.id));
  }, [assets, addAssetType, todayState, parkId, myLocationRequests]);

  const submitAddLocation = async () => {
    if (!addAssetId) return toast.error("Select an asset / work location");
    const remarks = (addLocationRemarks || "").trim();
    if (!remarks) return toast.error("Business justification / remarks are mandatory");

    const targetAsset = assets.find((x) => x.id === addAssetId);
    if (!targetAsset) return;

    setSubmittingAddLocation(true);
    try {
      const payload = {
        request_type: "add_work_location",
        park_id: parkId,
        asset_id: targetAsset.id,
        reason: remarks,
      };
      await api.post("/pmc/cleaning-location-requests", payload);
      toast.success(`Work location request submitted for ${targetAsset.name} (Pending Approval)`);

      try {
        const reqs = await api.get("/pmc/cleaning-location-requests/mine").then((r) => r.data || []);
        setMyLocationRequests(reqs);
      } catch {}

      setAddLocationOpen(false);
      setAddAssetId("");
      setAddLocationRemarks("");
    } catch (err) {
      toast.error(errorMsg(err, "Failed to submit work location request"));
    } finally {
      setSubmittingAddLocation(false);
    }
  };


  // -------- Custom CleaningQuestion CMS integration --------
  // Admin-defined questions are merged into the Before/After forms below.
  // disabledBuiltins tells us which built-in built-in fields the admin has
  // toggled off for this kind (e.g. hide uniform_ok / waste_removed).
  const [customQuestions, setCustomQuestions] = useState({ before: [], after: [] });
  const [disabledBuiltins, setDisabledBuiltins] = useState({ before: [], after: [] });
  const [customAnswers, setCustomAnswers] = useState({ before: {}, after: {} });

  const isBuiltinDisabled = (kind, key) => (disabledBuiltins[kind] || []).includes(key);

  const setCustomAnswer = (kind, qid, patch) => {
    setCustomAnswers((prev) => ({
      ...prev,
      [kind]: {
        ...(prev[kind] || {}),
        [qid]: { ...(prev[kind]?.[qid] || {}), ...patch },
      },
    }));
  };

  const validateCustomAnswers = (kind) => {
    const list = customQuestions[kind] || [];
    for (const q of list) {
      if (!q.active) continue;
      const ans = customAnswers[kind]?.[q.id] || {};
      const val = ans.value;
      if (q.required) {
        const empty =
          val === undefined ||
          val === null ||
          (typeof val === "string" && val.trim() === "");
        if (empty) {
          return `Please answer: ${q.label}`;
        }
      }
      if (
        q.question_type === "yes_no"
        && q.reason_when_value
        && String(val).toLowerCase() === String(q.reason_when_value).toLowerCase()
        && !(ans.reason && ans.reason.trim())
      ) {
        return `Please add a reason for: ${q.label}`;
      }
    }
    return null;
  };

  const buildCustomAnswersPayload = (kind) => {
    const list = customQuestions[kind] || [];
    return list
      .filter((q) => q.active)
      .map((q) => {
        const ans = customAnswers[kind]?.[q.id] || {};
        return {
          question_id: q.id,
          label: q.label,
          value: ans.value !== undefined && ans.value !== null ? String(ans.value) : null,
          reason: ans.reason ? String(ans.reason).trim() : null,
        };
      })
      .filter((a) => a.value !== null && a.value !== "");
  };

  // Bootstrap data
  useEffect(() => {
    if (!parkId) return;
    let alive = true;
    Promise.all([
      api.get("/parks").then((r) => (r.data || []).find((p) => p.id === parkId) || null),
      api.get("/park-assets", { params: { park_id: parkId } }).then((r) => r.data || []),
      api.get("/pmc/cleaning-checklists/today").then((r) => r.data || { by_park: {} }),
      api.get("/pmc/cleaning-location-requests/mine").then((r) => r.data || []).catch(() => []),
      api
        .get("/work-plans", { params: { park_id: parkId, start: todayISO(), end: todayISO() } })
        .then((r) => (r.data || [])[0] || null)
        .catch(() => null),
      // Pull today's visit status to auto-decide visit mode
      api
        .get("/park-visit-status/today")
        .then((r) => (r.data?.by_park || {})[parkId] || null)
        .catch(() => null),
    ]).then(([pk, a, td, myLocReqs, plan, visit]) => {
      if (!alive) return;
      setPark(pk);
      setAssets(a);
      setTodayState(td);
      setMyLocationRequests(myLocReqs);
      setTodayPlan(plan);
      // Pre-fill from plan
      if (plan?.asset_type) setBeforeAssetType(plan.asset_type);
      if (plan?.asset_id) {
        setBeforeAssetId(plan.asset_id);
        // Seed multi-select with the planned asset (asset metadata enriched
        // when the user resolves the dropdown). We'll look it up against the
        // freshly loaded assets list below.
        const planned = (a || []).find((x) => x.id === plan.asset_id);
        if (planned) {
          setBeforeAssetEntries([{
            asset_id: planned.id,
            asset_type: planned.asset_type,
            asset_name: planned.name,
          }]);
        }
      }
      // Cleaning checklists always use the visited-mode UI (full Before/After
      // flow with live photos, asset, uniform & tool-kit checks) regardless of
      // whether the PMC marked the park Not Visited today. The not-visited
      // status itself stays recorded separately via /park-visit-status; this
      // page just records the cleaning execution. An info banner below tells
      // the supervisor when the park is marked Not Visited.
      setVisitMode("visited");
      setParkNotVisitedToday(visit?.status === "not_visited");
    });
    // Custom cleaning questions CMS — non-blocking; failures keep the form
    // working with built-in fields only.
    api
      .get("/pmc/cleaning/questions")
      .then((r) => {
        if (!alive) return;
        const qs = r.data?.questions || [];
        setCustomQuestions({
          before: qs.filter((q) => q.kind === "before").sort((a, b) => (a.order || 0) - (b.order || 0)),
          after: qs.filter((q) => q.kind === "after").sort((a, b) => (a.order || 0) - (b.order || 0)),
        });
        setDisabledBuiltins({
          before: r.data?.disabled_builtins?.before || [],
          after: r.data?.disabled_builtins?.after || [],
        });
      })
      .catch(() => { /* graceful: keep built-ins only */ });
    return () => { alive = false; };
  }, [parkId]);

  const todayPark = todayState?.by_park?.[parkId] || { before: null, after: null };
  const beforeDone = !!todayPark.before;
  const afterDone = !!todayPark.after;

  // Cross-park sequencing gate: is there ANOTHER park where Before is done
  // but After is still pending? If so, starting Before here needs admin
  // approval. The other park's pending After-cleaning is left completely
  // untouched — this only decides whether THIS park's Before form shows.
  const otherPendingAfterPark = useMemo(() => {
    const byPark = todayState?.by_park || {};
    for (const pid of Object.keys(byPark)) {
      if (pid === parkId) continue;
      const rec = byPark[pid];
      if (rec?.before && !rec?.after) {
        return { park_id: pid, park_name: rec.before.park_name || rec.before.park_code || pid };
      }
    }
    return null;
  }, [todayState, parkId]);

  const myLocationRequestForThisPark = useMemo(() => {
    if (!otherPendingAfterPark) return null;
    return (
      myLocationRequests.find(
        (r) => r.new_park_id === parkId && r.pending_park_id === otherPendingAfterPark.park_id,
      ) || null
    );
  }, [myLocationRequests, otherPendingAfterPark, parkId]);

  const locationSwitchApproved = myLocationRequestForThisPark?.status === "approved";
  const needsLocationApproval = !parkNotVisitedToday && !!otherPendingAfterPark && !beforeDone && !locationSwitchApproved;

  const requestLocationSwitch = async () => {
    if (!otherPendingAfterPark) return;
    const reasonStr = (locationRequestReason || "").trim();
    if (!reasonStr) {
      toast.error("Remarks / business justification is required");
      return;
    }
    setLocationRequestBusy(true);
    try {
      const { data } = await api.post("/pmc/cleaning-location-requests", {
        new_park_id: parkId,
        reason: reasonStr,
      });
      setMyLocationRequests((prev) => [data, ...prev.filter((r) => r.id !== data.id)]);
      toast.success("Request sent. Admin will review shortly.");
      setLocationRequestOpen(false);
      setLocationRequestReason("");
    } catch (e) {
      toast.error(errorMsg(e, "Failed to send request"));
    } finally {
      setLocationRequestBusy(false);
    }
  };

  // -------- Offline-safe draft persistence --------
  // We snapshot the in-flight Before/After form to localStorage so a network
  // drop, page reload, or logout/login restores the supervisor's work
  // exactly. Photos, asset selections, answers — everything except the
  // final submit POST. The draft is wiped only when the matching submit
  // succeeds.
  const beforeDraftKey = draftKey({ parkId, date: todayISO(), kind: "before", supervisorId: "_" });
  const afterDraftKey  = draftKey({ parkId, date: todayISO(), kind: "after",  supervisorId: "_" });
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Restore the matching draft when the supervisor opens the page. Skipped
  // if the corresponding step has already been submitted to the server
  // (no point re-populating a finished section).
  useEffect(() => {
    if (!parkId) return;
    if (!beforeDone) {
      const d = loadDraft(beforeDraftKey);
      if (d) {
        if (Array.isArray(d.beforeAssetEntries)) setBeforeAssetEntries(d.beforeAssetEntries);
        if (d.visitMode) setVisitMode(d.visitMode);
        if (d.uniformOk !== undefined) setUniformOk(d.uniformOk);
        if (typeof d.uniformRemark === "string") setUniformRemark(d.uniformRemark);
        if (d.toolkitOk !== undefined) setToolkitOk(d.toolkitOk);
        if (typeof d.toolkitRemark === "string") setToolkitRemark(d.toolkitRemark);
        if (typeof d.notVisitedReason === "string") setNotVisitedReason(d.notVisitedReason);
        if (Array.isArray(d.beforePhotos)) setBeforePhotos(d.beforePhotos);
        if (Array.isArray(d.workerPhotos)) setWorkerPhotos(d.workerPhotos);
        if (d.customAnswersBefore) setCustomAnswers((prev) => ({ ...prev, before: d.customAnswersBefore }));
      }
    }
    if (beforeDone && !afterDone) {
      const d = loadDraft(afterDraftKey);
      if (d) {
        if (typeof d.completionMode === "string") setCompletionMode(d.completionMode);
        if (typeof d.lengthCleaned === "string") setLengthCleaned(d.lengthCleaned);
        if (d.afterPerAssetLengths) setAfterPerAssetLengths(d.afterPerAssetLengths);
        if (d.asPerPlan !== undefined) setAsPerPlan(d.asPerPlan);
        if (typeof d.notCompletedAsPerPlanReason === "string") setNotCompletedAsPerPlanReason(d.notCompletedAsPerPlanReason);
        if (typeof d.afterRemarks === "string") setAfterRemarks(d.afterRemarks);
        if (Array.isArray(d.afterPhotos)) setAfterPhotos(d.afterPhotos);
        if (d.wasteRemoved !== undefined) setWasteRemoved(d.wasteRemoved);
        if (typeof d.wasteNotRemovedReason === "string") setWasteNotRemovedReason(d.wasteNotRemovedReason);
        if (d.customAnswersAfter) setCustomAnswers((prev) => ({ ...prev, after: d.customAnswersAfter }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkId, beforeDone, afterDone]);

  // Save Before-draft on every change (debounced via 800ms timer). Skipped
  // when the Before step has already been submitted.
  useEffect(() => {
    if (!parkId || beforeDone) return;
    const t = setTimeout(() => {
      saveDraft(beforeDraftKey, {
        beforeAssetEntries, visitMode, uniformOk, uniformRemark,
        toolkitOk, toolkitRemark, notVisitedReason, beforePhotos,
        workerPhotos,
        customAnswersBefore: customAnswers.before,
      });
      setDraftSavedAt(Date.now());
    }, 800);
    return () => clearTimeout(t);
  }, [
    parkId, beforeDone, beforeDraftKey,
    beforeAssetEntries, visitMode, uniformOk, uniformRemark,
    toolkitOk, toolkitRemark, notVisitedReason, beforePhotos,
    workerPhotos,
    customAnswers.before,
  ]);

  // Save After-draft on every change. Skipped until Before is done and once
  // After itself has been submitted.
  useEffect(() => {
    if (!parkId || !beforeDone || afterDone) return;
    const t = setTimeout(() => {
      saveDraft(afterDraftKey, {
        completionMode, lengthCleaned, afterPerAssetLengths,
        asPerPlan, notCompletedAsPerPlanReason, afterRemarks, afterPhotos,
        wasteRemoved, wasteNotRemovedReason,
        customAnswersAfter: customAnswers.after,
      });
      setDraftSavedAt(Date.now());
    }, 800);
    return () => clearTimeout(t);
  }, [
    parkId, beforeDone, afterDone, afterDraftKey,
    completionMode, lengthCleaned, afterPerAssetLengths,
    asPerPlan, notCompletedAsPerPlanReason, afterRemarks, afterPhotos,
    wasteRemoved, wasteNotRemovedReason,
    customAnswers.after,
  ]);
  // Roads require an "Approx. length cleaned (km)" value so cycle-progress
  // tracking on the Park Pulse Atlas reflects real distance. We enforce this
  // both client-side (blocks submit + visible red asterisk) and server-side.
  // OSR assets reuse the same numeric field but the label/unit reads
  // "extent (acres)" — the value is stored verbatim into length_km_cleaned
  // (the column is generic numeric storage, not an actual km-only measure).
  const beforeAssetTypeKey = (todayPark.before?.asset_type || "").toLowerCase();
  const isRoadAsset = beforeAssetTypeKey === "road";
  const isOsrAsset = beforeAssetTypeKey === "osr";
  const sizeUnitLabel = isOsrAsset
    ? "Approx. extent cleaned (acres)"
    : "Approx. length cleaned (km)";
  const sizeUnitSuffix = isOsrAsset ? "acres" : "km";
  const sizeUnitPlaceholder = isRoadAsset
    ? "Required for roads — e.g., 0.5"
    : isOsrAsset
      ? "e.g., 1.25 (acres)"
      : "e.g., 1.25";
  const lengthCleanedNum = Number(lengthCleaned);
  // Multi-asset mode (more than one Before asset_entry): require km input
  // on every ROAD asset via the per-asset map. Single-asset mode falls
  // back to the legacy single `lengthCleaned` input.
  const beforeEntriesForGate = todayPark.before?.asset_entries || [];
  const lengthRequiredMissing = (() => {
    if (beforeEntriesForGate.length > 1) {
      // Multi-asset: any ROAD asset without a positive km value blocks submit.
      const missingRoads = beforeEntriesForGate.filter((e) => {
        if ((e.asset_type || "").toLowerCase() !== "road") return false;
        const raw = afterPerAssetLengths[e.asset_id];
        const n = Number(raw);
        return raw === "" || raw == null || !Number.isFinite(n) || n <= 0;
      });
      return missingRoads.length > 0;
    }
    // Single-asset: legacy check on the single input.
    return isRoadAsset && (!lengthCleaned || !Number.isFinite(lengthCleanedNum) || lengthCleanedNum <= 0);
  })();

  const assetOptions = useMemo(
    () => assets.filter((a) => a.asset_type === beforeAssetType),
    [assets, beforeAssetType],
  );

  // ---- BEFORE submit ----
  // When capturing/uploading photos for a specific asset in multi-asset mode,
  // we tag each photo with its asset_id so per-asset panels render correctly.
  const [currentBeforeAssetId, setCurrentBeforeAssetId] = useState(null);

  const handleBeforeCapture = async (b64, overrideAssetId = null) => {
    setShowBeforeCamera(false);
    if (!b64) return;
    const aid = overrideAssetId || currentBeforeAssetId || (beforeAssetEntries.length === 1 ? beforeAssetEntries[0].asset_id : null);
    try {
      const loc = await getCurrentLocation();
      setBeforePhotos((prev) => [
        ...prev,
        { photo_b64: b64, source: "live_camera", asset_id: aid, gps_lat: loc?.lat, gps_lng: loc?.long },
      ]);
    } catch {
      setBeforePhotos((prev) => [...prev, { photo_b64: b64, source: "live_camera", asset_id: aid }]);
    }
  };

  const handleBeforeGalleryUpload = async (e, overrideAssetId = null) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const aid = overrideAssetId || currentBeforeAssetId || (beforeAssetEntries.length === 1 ? beforeAssetEntries[0].asset_id : null);
    try {
      const loc = await getCurrentLocation();
      const stamped = [];
      for (const f of files.slice(0, 5)) {
        const url = await stampGallery(f);
        stamped.push({ photo_b64: url, source: "gallery_upload", asset_id: aid, gps_lat: loc?.lat, gps_lng: loc?.long });
      }
      // For multi-asset visited mode, allow up to 5 photos PER asset.
      // For remote mode (aid==null) keep the legacy 5-total cap.
      if (aid) {
        setBeforePhotos((prev) => {
          const ownCount = prev.filter((p) => p.asset_id === aid).length;
          const left = Math.max(0, 5 - ownCount);
          return [...prev, ...stamped.slice(0, left)];
        });
      } else {
        setBeforePhotos((prev) => [...prev, ...stamped].slice(0, 5));
      }
    } catch {
      toast.error("Could not read images");
    } finally {
      e.target.value = "";
    }
  };

  // -------- Workers-at-work capture handlers --------
  // Live camera capture. GPS is required by backend; we surface a friendly
  // toast if it cannot be obtained so the supervisor knows the capture is
  // unusable until they enable location.
  const handleWorkerCapture = async (b64) => {
    setShowWorkerCamera(false);
    if (!b64) return;
    try {
      const loc = await getCurrentLocation();
      if (!loc?.lat || !loc?.long) {
        toast.error("GPS required for workers-at-work photo. Enable location and try again.");
        return;
      }
      setWorkerPhotos((prev) => [
        ...prev,
        { photo_b64: b64, source: "live_camera", gps_lat: loc.lat, gps_lng: loc.long },
      ].slice(0, 4));
    } catch {
      toast.error("GPS required for workers-at-work photo. Enable location and try again.");
    }
  };

  const handleWorkerGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      let lat = null, lng = null;
      try {
        const loc = await getCurrentLocation();
        if (loc?.lat && loc?.long) { lat = loc.lat; lng = loc.long; }
      } catch {}
      if (!lat && park?.latitude) lat = Number(park.latitude);
      if (!lng && park?.longitude) lng = Number(park.longitude);
      if (!lat) lat = 13.0827;
      if (!lng) lng = 80.2707;
      const stamped = [];
      for (const f of files.slice(0, 4)) {
        const url = await stampGallery(f);
        stamped.push({ photo_b64: url, source: "gallery_upload", gps_lat: lat, gps_lng: lng });
      }
      setWorkerPhotos((prev) => [...prev, ...stamped].slice(0, 4));
    } catch {
      toast.error("Could not read images");
    } finally {
      e.target.value = "";
    }
  };

  const submitBefore = async () => {
    if (beforeDone) return;
    if (visitMode === "visited") {
      if (beforeAssetEntries.length === 0)
        return toast.error("Pick at least one asset planned for cleaning");
      if (!isBuiltinDisabled("before", "uniform_ok") && uniformOk == null)
        return toast.error("Mark uniform check (Yes/No)");
      if (!isBuiltinDisabled("before", "uniform_ok") && uniformOk === false && !uniformRemark.trim())
        return toast.error("Please add a remark explaining the uniform issue");
      if (!isBuiltinDisabled("before", "toolkit_ok") && toolkitOk == null)
        return toast.error("Mark tool kit check (Yes/No)");
      if (!isBuiltinDisabled("before", "toolkit_ok") && toolkitOk === false && !toolkitRemark.trim())
        return toast.error("Please add a remark explaining the tool kit issue");
      if (!isBuiltinDisabled("before", "photos")) {
        for (const e of beforeAssetEntries) {
          const areaLabel = ASSET_TYPE_LABEL[e.asset_type] || e.asset_type || "asset";
          const cnt = beforePhotos.filter((p) => p.asset_id === e.asset_id).length;
          if (cnt < 3) return toast.error(`Add at least 3 before-photos for ${areaLabel} (${e.asset_name})`);
          if (cnt > 5) return toast.error(`Max 5 before-photos for ${areaLabel} (${e.asset_name})`);
        }
      }
    } else {
      if (!isBuiltinDisabled("before", "photos") && beforePhotos.length < 1)
        return toast.error("Add at least 1 photo");
    }
    // Workers-at-work proof — mandatory 1..4 photos regardless of mode.
    if (workerPhotos.length < 1)
      return toast.error("Capture at least 1 workers-at-work photo");
    if (workerPhotos.length > 4)
      return toast.error("Maximum 4 workers-at-work photos");
    // GPS is required server-side; surface a clean message if missing.
    if (workerPhotos.some((p) => p.gps_lat == null || p.gps_lng == null))
      return toast.error("Workers-at-work photos require GPS coordinates");
    const customErr = validateCustomAnswers("before");
    if (customErr) return toast.error(customErr);
    setSubmittingBefore(true);
    try {
      const primary = beforeAssetEntries[0];
      const payload = {
        park_id: parkId,
        visit_mode: visitMode,
        asset_type: visitMode === "visited" && primary ? primary.asset_type : null,
        asset_id: visitMode === "visited" && primary ? primary.asset_id : null,
        asset_entries: visitMode === "visited" ? beforeAssetEntries.map((e) => ({
          asset_id: e.asset_id,
          asset_type: e.asset_type,
          asset_name: e.asset_name,
        })) : [],
        uniform_ok: visitMode === "visited" && !isBuiltinDisabled("before", "uniform_ok") ? uniformOk : null,
        uniform_remark: visitMode === "visited" && !isBuiltinDisabled("before", "uniform_ok") ? uniformRemark || null : null,
        toolkit_ok: visitMode === "visited" && !isBuiltinDisabled("before", "toolkit_ok") ? toolkitOk : null,
        toolkit_remark: visitMode === "visited" && !isBuiltinDisabled("before", "toolkit_ok") ? toolkitRemark || null : null,
        not_visited_reason: visitMode === "remote" ? notVisitedReason || null : null,
        photos: beforePhotos,
        workers_at_work_photos: workerPhotos,
        custom_answers: buildCustomAnswersPayload("before"),
      };
      const { data } = await api.post("/pmc/cleaning-checklists/before", payload);
      toast.success("Before-cleaning checklist saved");
      clearDraft(beforeDraftKey);
      setTodayState((s) => ({
        ...s,
        by_park: {
          ...s.by_park,
          [parkId]: { ...(s.by_park?.[parkId] || {}), before: { ...data, photo_count: beforePhotos.length, photos: undefined } },
        },
      }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "cleaning_location_pending_after") {
        // Defensive fallback in case client state was stale (e.g. the
        // approval was revoked in another tab) — the banner normally
        // prevents reaching this endpoint without an approved request.
        toast.error(errorMsg(e, "Admin approval is needed to start this location"));
      } else {
        toast.error(errorMsg(e, "Submit failed"));
      }
    } finally {
      setSubmittingBefore(false);
    }
  };

  // ---- AFTER submit ----
  const [currentAfterAssetId, setCurrentAfterAssetId] = useState(null);

  const handleAfterCapture = async (b64, overrideAssetId = null) => {
    setShowAfterCamera(false);
    if (!b64) return;
    const beforeEntries = todayPark.before?.asset_entries || [];
    const aid = overrideAssetId || currentAfterAssetId || (beforeEntries.length === 1 ? beforeEntries[0].asset_id : null);
    try {
      const loc = await getCurrentLocation();
      setAfterPhotos((prev) => [
        ...prev,
        { photo_b64: b64, source: "live_camera", asset_id: aid, gps_lat: loc?.lat, gps_lng: loc?.long },
      ]);
    } catch {
      setAfterPhotos((prev) => [...prev, { photo_b64: b64, source: "live_camera", asset_id: aid }]);
    }
  };

  const handleAfterGalleryUpload = async (e, overrideAssetId = null) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const beforeEntries = todayPark.before?.asset_entries || [];
    const aid = overrideAssetId || currentAfterAssetId || (beforeEntries.length === 1 ? beforeEntries[0].asset_id : null);
    try {
      const loc = await getCurrentLocation();
      const stamped = [];
      for (const f of files.slice(0, 4)) {
        const url = await stampGallery(f);
        stamped.push({ photo_b64: url, source: "gallery_upload", asset_id: aid, gps_lat: loc?.lat, gps_lng: loc?.long });
      }
      if (aid) {
        setAfterPhotos((prev) => {
          const ownCount = prev.filter((p) => p.asset_id === aid).length;
          const left = Math.max(0, 4 - ownCount);
          return [...prev, ...stamped.slice(0, left)];
        });
      } else {
        setAfterPhotos((prev) => [...prev, ...stamped].slice(0, 4));
      }
    } catch {
      toast.error("Could not read images");
    } finally {
      e.target.value = "";
    }
  };

  const submitAfter = async () => {
    if (afterDone) return;
    if (!beforeDone) return toast.error("Submit Before-cleaning first");
    const beforeEntries = todayPark.before?.asset_entries || [];

    if (!isBuiltinDisabled("after", "photos")) {
      if (visitMode !== "remote") {
        for (const e of beforeEntries) {
          const areaLabel = ASSET_TYPE_LABEL[e.asset_type] || e.asset_type || "asset";
          const cnt = afterPhotos.filter((p) => p.asset_id === e.asset_id).length;
          if (cnt < 1) return toast.error(`Add at least 1 after-photo for ${areaLabel} (${e.asset_name || "location"})`);
          if (cnt > 4) return toast.error(`Max 4 after-photos for ${areaLabel} (${e.asset_name || "location"})`);
        }
      } else {
        if (afterPhotos.length < 1) return toast.error("At least 1 after-cleaning photo required");
        if (afterPhotos.length > 4) return toast.error("Maximum 4 after-cleaning photos");
      }
    }
    if (!isBuiltinDisabled("after", "completed_as_per_plan")) {
      if (asPerPlan == null) return toast.error("Mark whether activity completed as per plan");
      if (asPerPlan === false && !notCompletedAsPerPlanReason.trim())
        return toast.error("Please provide a reason why cleaning activity was not completed as per plan");
    }

    let perAssetPayload = [];
    if (!isBuiltinDisabled("after", "length_km_cleaned")) {
      if (beforeEntries.length > 1) {
        const missingRoads = beforeEntries.filter((e) => {
          const v = afterPerAssetLengths[e.asset_id];
          const n = Number(v);
          return (e.asset_type || "").toLowerCase() === "road"
            && (v === "" || v == null || !Number.isFinite(n) || n <= 0);
        });
        if (missingRoads.length) {
          return toast.error(
            `Enter length cleaned (km) for: ${missingRoads.map((e) => e.asset_name).join(", ")}`,
          );
        }
        perAssetPayload = beforeEntries.map((e) => ({
          asset_id: e.asset_id,
          asset_type: e.asset_type,
          asset_name: e.asset_name,
          length_km_cleaned: afterPerAssetLengths[e.asset_id] !== undefined
            && afterPerAssetLengths[e.asset_id] !== ""
            ? Number(afterPerAssetLengths[e.asset_id]) : null,
        }));
      } else if (lengthRequiredMissing) {
        return toast.error("Approx. length cleaned (km) is required for road cleaning captures");
      }
    }

    if (!isBuiltinDisabled("after", "waste_removed")) {
      if (wasteRemoved == null) return toast.error("Please answer the waste-removal question (Yes/No)");
      if (wasteRemoved === false && !wasteNotRemovedReason.trim())
        return toast.error("Please provide a brief reason why the cleaned waste was not removed");
    }
    const customErr = validateCustomAnswers("after");
    if (customErr) return toast.error(customErr);
    setSubmittingAfter(true);
    try {
      const payload = {
        park_id: parkId,
        completion_mode: !isBuiltinDisabled("after", "completion_mode") ? (completionMode || null) : null,
        length_km_cleaned: !isBuiltinDisabled("after", "length_km_cleaned")
          && perAssetPayload.length === 0 && lengthCleaned ? Number(lengthCleaned) : null,
        asset_entries: perAssetPayload,
        completed_as_per_plan: !isBuiltinDisabled("after", "completed_as_per_plan") ? asPerPlan : null,
        not_completed_as_per_plan_reason: !isBuiltinDisabled("after", "completed_as_per_plan") && asPerPlan === false ? notCompletedAsPerPlanReason.trim() : null,
        remarks: afterRemarks || null,
        waste_removed: !isBuiltinDisabled("after", "waste_removed") ? wasteRemoved : null,
        waste_not_removed_reason: !isBuiltinDisabled("after", "waste_removed") && wasteRemoved === false ? wasteNotRemovedReason.trim() : null,
        photos: afterPhotos,
        custom_answers: buildCustomAnswersPayload("after"),
      };
      const { data } = await api.post("/pmc/cleaning-checklists/after", payload);
      toast.success("After-cleaning checklist saved");
      clearDraft(afterDraftKey);
      setTodayState((s) => ({
        ...s,
        by_park: {
          ...s.by_park,
          [parkId]: { ...(s.by_park?.[parkId] || {}), after: { ...data, photo_count: afterPhotos.length, photos: undefined } },
        },
      }));
    } catch (e) {
      toast.error(errorMsg(e, "Submit failed"));
    } finally {
      setSubmittingAfter(false);
    }
  };

  if (!parkId) {
    return (
      <AppShell title="Cleaning checklist" subtitle="Pick a park from Today">
        <Button onClick={() => navigate("/pmc/today")}><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button>
      </AppShell>
    );
  }

  const headerSubtitle = park ? `${park.code} — ${park.name}` : "Loading…";
  const planLabel = todayPlan?.location
    ? `${todayPlan.work_type?.toUpperCase() || ""} · ${todayPlan.overridden_location || todayPlan.location}`
    : null;

  return (
    <AppShell title="Cleaning checklist" subtitle={headerSubtitle}>
      <div className="flex items-center justify-between gap-2">
        <Link to="/pmc/today"><Button variant="outline" size="sm"><ArrowLeft className="mr-2 h-4 w-4" /> Back</Button></Link>
        <div className="flex items-center gap-3">
          {draftSavedAt && !beforeDone || (draftSavedAt && beforeDone && !afterDone) ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] text-emerald-700 ring-1 ring-emerald-200"
              title="Your work is auto-saved on this device. Photos and answers will be restored even if you lose network or log out."
              data-testid="pmc-clean-draft-indicator"
            >
              <CheckCircle2 className="h-3 w-3" />
              Draft saved
            </span>
          ) : null}
          <div className="text-xs text-muted-foreground">{fmtDate(new Date())}</div>
        </div>
      </div>

      {/* Plan strip */}
      {planLabel ? (
        <Card className="border-amber-200 bg-amber-50/40 mt-3">
          <CardContent className="flex flex-wrap items-center gap-2 p-3 text-xs">
            <Sparkles className="h-4 w-4 text-amber-700" />
            <span className="text-amber-900">Today&apos;s plan: <span className="font-medium">{planLabel}</span></span>
          </CardContent>
        </Card>
      ) : null}

      {/* Stage indicator — sequential: Before → After (no tabs) */}
      <div className="mt-3 flex items-center gap-2 text-[12px]" data-testid="pmc-clean-stage-indicator">
        <button
          type="button"
          onClick={() => {
            if (beforeDone) {
              const created = todayPark.before?.created_at
                ? new Date(todayPark.before.created_at).getTime() : null;
              const left = created ? Math.max(0, Math.floor((created + 10 * 60 * 1000 - nowTs) / 1000)) : 0;
              if (left <= 0) {
                setResetBeforeOpen(true);
              }
            }
          }}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${
            beforeDone ? "bg-emerald-50 text-emerald-800 border border-emerald-200 cursor-pointer hover:bg-emerald-100/70" : "bg-primary/10 text-primary border border-primary/30"
          }`}
          title={beforeDone ? "Before-cleaning submitted (Click to request reset approval if window expired)" : "1. Before-cleaning"}
        >
          {beforeDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="grid h-4 w-4 place-items-center rounded-full bg-primary text-[10px] text-primary-foreground">1</span>}
          Before
        </button>
        <div className="h-px flex-1 bg-slate-200" />
        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 ${afterDone ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : beforeDone ? "bg-primary/10 text-primary border border-primary/30" : "bg-slate-100 text-slate-400 border border-slate-200"}`}>
          {afterDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] ${beforeDone ? "bg-primary text-primary-foreground" : "bg-slate-300 text-white"}`}>2</span>}
          After
        </span>
      </div>

      {/* Park "Not Visited" info banner — cleaning checklist is still allowed
          with the full visited-mode UI; this is just a context reminder. */}
      {parkNotVisitedToday && !afterDone && (
        <Card className="border-sky-200 bg-sky-50/60 mt-3">
          <CardContent className="flex items-start gap-2 p-3 text-[12px] text-sky-900" data-testid="pmc-clean-not-visited-banner">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              This park is marked <strong>Not Visited</strong> for today. You can still file the
              Before / After cleaning checklist normally — the visit status itself is recorded
              separately and admin will see both.
            </div>
          </CardContent>
        </Card>
      )}

      {/* BEFORE — shown until completed; then AFTER takes over */}
      {!beforeDone && (
        <Card className="mt-3">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">1. Before-cleaning</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {visitMode === "visited" ? (
              <>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-xs">Area type (filter)</Label>
                    <Select value={beforeAssetType} onValueChange={(v) => { setBeforeAssetType(v); setBeforeAssetId(""); }}>
                      <SelectTrigger data-testid="pmc-clean-asset-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.keys(ASSET_TYPE_LABEL).map((k) => (
                          <SelectItem key={k} value={k}>{ASSET_TYPE_LABEL[k]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Add asset to today&apos;s cleaning *</Label>
                    <Select
                      value="_picker"
                      onValueChange={(v) => {
                        if (v === "_picker" || v === "_none") return;
                        const a = assets.find((x) => x.id === v);
                        if (!a) return;
                        if (beforeAssetEntries.some((e) => e.asset_id === a.id)) return;
                        setBeforeAssetEntries((prev) => [
                          ...prev,
                          { asset_id: a.id, asset_type: a.asset_type, asset_name: a.name },
                        ]);
                        setBeforeAssetId(beforeAssetEntries.length === 0 ? a.id : beforeAssetId);
                      }}
                    >
                      <SelectTrigger data-testid="pmc-clean-asset-id">
                        <SelectValue placeholder={assetOptions.length === 0 ? "No assets" : "+ Pick an asset"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_picker" disabled>+ Pick an asset to add</SelectItem>
                        {assetOptions.length === 0 ? (
                          <SelectItem value="_none">— No assets configured —</SelectItem>
                        ) : assetOptions
                            .filter((a) => !beforeAssetEntries.some((e) => e.asset_id === a.id))
                            .map((a) => (
                              <SelectItem key={a.id} value={a.id}>
                                {a.name}
                                {a.length_km != null
                                  ? ` (${a.length_km} ${a.asset_type === "osr" ? "acres" : "km"})`
                                  : ""}
                              </SelectItem>
                            ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Selected assets — chips */}
                {beforeAssetEntries.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5" data-testid="pmc-clean-asset-chips">
                    {beforeAssetEntries.map((e) => (
                      <span
                        key={e.asset_id}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-900"
                      >
                        <span className="text-[10px] uppercase tracking-wide text-emerald-700">
                          {ASSET_TYPE_LABEL[e.asset_type] || e.asset_type}
                        </span>
                        · {e.asset_name}
                        <button
                          type="button"
                          onClick={() => {
                            const removedId = e.asset_id;
                            setBeforeAssetEntries((prev) => prev.filter((x) => x.asset_id !== removedId));
                            setBeforePhotos((all) => all.filter((p) => p.asset_id !== removedId));
                            setAfterPerAssetLengths((prev) => {
                              const copy = { ...prev };
                              delete copy[removedId];
                              return copy;
                            });
                            if (beforeAssetId === removedId) {
                              setBeforeAssetId("");
                            }
                          }}
                          className="ml-0.5 rounded-full p-0.5 text-emerald-700 hover:bg-emerald-200/60"
                          aria-label={`Remove ${e.asset_name}`}
                          data-testid={`pmc-clean-asset-chip-remove-${e.asset_id}`}
                        >
                          <XIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Pick one or more assets covered by today&apos;s cleaning. You can mix roads, OSR plots and other types.
                  </p>
                )}

                <div className="space-y-3">
                  {!isBuiltinDisabled("before", "uniform_ok") && (
                    <div>
                      <Label className="text-xs">All workers in proper uniform? *</Label>
                      <div className="mt-1"><YesNoToggle value={uniformOk} onChange={setUniformOk} testid="pmc-clean-uniform" /></div>
                      <Textarea
                        rows={2}
                        placeholder={uniformOk === false ? "Remark (required) — explain the uniform issue…" : "Optional remark…"}
                        value={uniformRemark}
                        onChange={(e) => setUniformRemark(e.target.value)}
                        className="mt-2"
                        data-testid="pmc-clean-uniform-remark"
                      />
                    </div>
                  )}
                  {!isBuiltinDisabled("before", "toolkit_ok") && (
                    <div>
                      <Label className="text-xs">All tool kits provided? *</Label>
                      <div className="mt-1"><YesNoToggle value={toolkitOk} onChange={setToolkitOk} testid="pmc-clean-toolkit" /></div>
                      <Textarea
                        rows={2}
                        placeholder={toolkitOk === false ? "Remark (required) — explain the tool kit issue…" : "Optional remark…"}
                        value={toolkitRemark}
                        onChange={(e) => setToolkitRemark(e.target.value)}
                        className="mt-2"
                        data-testid="pmc-clean-toolkit-remark"
                      />
                    </div>
                  )}
                  <CustomQuestionsRenderer
                    kind="before"
                    questions={customQuestions.before}
                    answers={customAnswers.before}
                    setAnswer={(qid, patch) => setCustomAnswer("before", qid, patch)}
                  />
                </div>

                {!isBuiltinDisabled("before", "photos") && (
                  beforeAssetEntries.length > 0 ? (
                    <div className="space-y-3" data-testid="pmc-clean-before-multi">
                      <div className="text-xs font-medium text-slate-600">
                        Before-cleaning photos (per area type / asset, 3–5 each){useGalleryForPhotos ? " — gallery upload" : ""}
                      </div>
                      {beforeAssetEntries.map((e) => {
                        const ownPhotos = beforePhotos.filter((p) => p.asset_id === e.asset_id);
                        const areaLabel = ASSET_TYPE_LABEL[e.asset_type] || e.asset_type || "Area";
                        return (
                          <div key={e.asset_id} className="rounded-md border bg-slate-50/40 p-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <div className="text-sm font-medium text-slate-700" data-testid={`pmc-clean-before-asset-${e.asset_id}`}>
                                {areaLabel} – Before Cleaning ({e.asset_name})
                              </div>
                              <Badge variant="secondary" className="text-[10px]">{areaLabel}</Badge>
                            </div>
                            <CapturePanel
                              label={`Before-cleaning photos (${areaLabel})`}
                              helper={useGalleryForPhotos ? "3 to 5 photos (gallery upload)" : "3 to 5 photos (live camera)"}
                              photos={ownPhotos}
                              onRemove={(localIdx) => {
                                const target = ownPhotos[localIdx];
                                setBeforePhotos((all) => all.filter((p) => p !== target));
                              }}
                              onZoom={(s) => setLightboxSrc(s)}
                              galleryUpload={useGalleryForPhotos}
                              onGalleryFiles={(ev) => { setCurrentBeforeAssetId(e.asset_id); handleBeforeGalleryUpload(ev, e.asset_id); }}
                              onOpenCamera={() => { setCurrentBeforeAssetId(e.asset_id); setShowBeforeCamera(true); }}
                              maxPhotos={5}
                              testidPrefix={`pmc-clean-before-${e.asset_id}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3 text-xs text-amber-900" data-testid="pmc-clean-no-asset-selected">
                      Please select an Area type and pick an asset above to display the photo capture section for each area type.
                    </div>
                  )
                )}
              </>
            ) : (
              <>
                <Textarea
                  placeholder="Reason for not visiting (optional)…"
                  rows={2}
                  value={notVisitedReason}
                  onChange={(e) => setNotVisitedReason(e.target.value)}
                  data-testid="pmc-clean-not-visited-reason"
                />
                <CapturePanel
                  label="Cleaning evidence photos"
                  helper="1 to 5 photos (gallery upload)"
                  photos={beforePhotos}
                  onRemove={(i) => setBeforePhotos((p) => p.filter((_, idx) => idx !== i))}
                  onZoom={(s) => setLightboxSrc(s)}
                  galleryUpload
                  onGalleryFiles={handleBeforeGalleryUpload}
                  maxPhotos={5}
                  testidPrefix="pmc-clean-before-gallery-panel"
                />
              </>
            )}

            {/* Workers-at-work proof — mandatory for both visited & remote
                modes. Live camera when visited, gallery upload when not
                visited / remote. GPS is enforced both client and server. */}
            <div
              className="mt-1 rounded-md border border-sky-200 bg-sky-50/50 p-3"
              data-testid="pmc-clean-workers-section"
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-sky-900">
                    Workers at work photos
                  </div>
                  <div className="text-[11px] text-sky-800/80">
                    Capture 1–4 photos of workers actively cleaning today.
                    GPS is mandatory on every shot.
                  </div>
                </div>
                <span
                  className="inline-flex shrink-0 items-center rounded-full border border-sky-300 bg-white px-2 py-0.5 text-[10px] font-medium text-sky-800"
                  data-testid="pmc-clean-workers-count"
                >
                  {workerPhotos.length}/4
                </span>
              </div>
              <CapturePanel
                label="Workers at work"
                helper={
                  useGalleryForPhotos
                    ? "1 to 4 photos (gallery upload, GPS required)"
                    : "1 to 4 photos (live camera, GPS required)"
                }
                photos={workerPhotos}
                onRemove={(i) =>
                  setWorkerPhotos((p) => p.filter((_, idx) => idx !== i))
                }
                onZoom={(s) => setLightboxSrc(s)}
                galleryUpload={useGalleryForPhotos}
                onGalleryFiles={handleWorkerGalleryUpload}
                onOpenCamera={() => setShowWorkerCamera(true)}
                maxPhotos={4}
                testidPrefix="pmc-clean-workers-panel"
              />
            </div>

            <Button
              onClick={submitBefore}
              disabled={submittingBefore}
              size="lg"
              className="h-12 text-base"
              data-testid="pmc-clean-before-submit"
            >
              {submittingBefore ? "Submitting…" : "Submit Before-cleaning →"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Work Locations Summary + Add New Work Location button */}
      {beforeDone && (
        <Card className="mt-3 border-emerald-200 bg-emerald-50/40">
          <CardContent className="p-3.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-semibold text-emerald-950 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Work Locations ({(todayPark.before?.asset_entries || []).length || 1})
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="pmc-clean-work-locations-list">
                  {((todayPark.before?.asset_entries || []).length > 0
                    ? todayPark.before.asset_entries
                    : [{ asset_id: todayPark.before?.asset_id, asset_name: todayPark.before?.asset_name, asset_type: todayPark.before?.asset_type }]
                  ).map((e) => (
                    <Badge key={e.asset_id || e.asset_name} variant="outline" className="border-emerald-300 bg-white text-emerald-900 text-[11px]">
                      <span className="text-[10px] uppercase font-bold text-emerald-700 mr-1">{e.asset_type || "asset"}</span>
                      {e.asset_name || "Location"}
                    </Badge>
                  ))}

                  {/* Render Pending & Rejected Location Requests for this park */}
                  {(myLocationRequests || [])
                    .filter((r) => r.park_id === parkId && (r.request_type === "add_work_location" || r.asset_id))
                    .map((r) => {
                      if (r.status === "pending") {
                        return (
                          <Badge key={r.id} variant="outline" className="border-amber-300 bg-amber-50 text-amber-900 text-[11px] font-medium" title={`Business Justification: ${r.reason}`}>
                            <Clock className="mr-1 h-3 w-3 text-amber-600" />
                            <span className="text-[10px] uppercase font-bold text-amber-700 mr-1">{r.asset_type || "asset"}</span>
                            {r.asset_name || "Location"} (Pending Approval)
                          </Badge>
                        );
                      }
                      if (r.status === "rejected") {
                        return (
                          <Badge key={r.id} variant="outline" className="border-rose-300 bg-rose-50 text-rose-800 text-[11px] font-medium" title={`Rejected note: ${r.decision_note || r.reason}`}>
                            <XCircle className="mr-1 h-3 w-3 text-rose-600" />
                            <span className="text-[10px] uppercase font-bold text-rose-700 mr-1">{r.asset_type || "asset"}</span>
                            {r.asset_name || "Location"} (Rejected)
                          </Badge>
                        );
                      }
                      return null;
                    })}
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 border-emerald-400 bg-white text-xs font-medium text-emerald-800 hover:bg-emerald-100 shadow-sm"
                onClick={() => {
                  setAddLocationOpen(true);
                  setAddAssetId("");
                  setAddLocationRemarks("");
                }}
                data-testid="pmc-clean-add-location-btn"
              >
                <PlusCircle className="h-4 w-4 text-emerald-600" />
                Add New Work Location
              </Button>
            </div>
          </CardContent>
        </Card>
      )}



      {/* AFTER — only shown after Before is submitted */}
      {beforeDone && (
        <Card className="mt-3">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <CardTitle className="text-base">2. After-cleaning</CardTitle>
              {/* Two recovery affordances:
                  • Reset After  — visible when After is submitted and within
                    its 10-minute edit window.
                  • Reset Before — visible when Before is submitted and within
                    its 10-minute window. Refused server-side if After exists. */}
              <div className="flex flex-wrap items-center gap-1.5">
                {(() => {
                  const created = todayPark.before?.created_at
                    ? new Date(todayPark.before.created_at).getTime() : null;
                  const left = created ? Math.max(0, Math.floor((created + 10 * 60 * 1000 - nowTs) / 1000)) : 0;
                  // Legacy/broken-record escape hatch: when Before exists but
                  // has no valid asset binding, the 10-min window is
                  // bypassed so the supervisor can always recover.
                  const hasEntries = (todayPark.before?.asset_entries || []).length > 0;
                  const hasLegacy = !!todayPark.before?.asset_id;
                  const isBroken = todayPark.before && !hasEntries && !hasLegacy;
                  const allowed = todayPark.before && (left > 0 || isBroken);
                  if (!allowed) {
                    if (todayPark.before && left <= 0 && !isBroken) {
                      const existingReq = (myLocationRequests || []).find(
                        (r) => r.park_id === parkId && r.request_type === "reset_before"
                      );
                      const isPending = existingReq?.status === "pending";
                      return (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[11px] text-amber-800 border-amber-300 hover:bg-amber-50"
                          onClick={() => setResetBeforeOpen(true)}
                          data-testid="pmc-clean-request-reset-before"
                        >
                          <Lock className="mr-1 h-3 w-3 text-amber-600" />
                          {isPending ? "Reset Before (Pending Approval)" : "Reset Before (Request Approval)"}
                        </Button>
                      );
                    }
                    return null;
                  }
                  const disabled = !!todayPark.after && !isBroken;
                  return (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px] text-rose-700 hover:bg-rose-50"
                      disabled={disabled}
                      title={disabled
                        ? "Reset the After-cleaning record first"
                        : (isBroken ? "Legacy record without asset — reset allowed"
                          : `Reset window closes in ${Math.floor(left / 60)}m ${left % 60}s`)}
                      onClick={async () => {
                        if (!window.confirm("WARNING — this will permanently delete all Before-cleaning data (asset selection, uniform/toolkit checks, photos, custom answers) for today. Do you want to proceed?")) return;
                        try {
                          await api.delete(`/pmc/cleaning-checklists/before?park_id=${parkId}`);
                          clearDraft(beforeDraftKey);
                          clearDraft(afterDraftKey);
                          toast.success("Before-cleaning reset — please resubmit");
                          window.location.reload();
                        } catch (err) {
                          const detail = err?.response?.data?.detail;
                          toast.error(typeof detail === "string" ? detail : "Reset failed");
                        }
                      }}
                      data-testid="pmc-clean-reset-before"
                    >
                      {isBroken ? "Reset Before" : `Reset Before · ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`}
                    </Button>
                  );
                })()}
                {(() => {
                  const created = todayPark.after?.created_at
                    ? new Date(todayPark.after.created_at).getTime() : null;
                  const left = created ? Math.max(0, Math.floor((created + 10 * 60 * 1000 - nowTs) / 1000)) : 0;
                  // Legacy/broken: allow After reset always when the Before
                  // is broken too (recovery path for the Gangaikondan case).
                  const hasEntries = (todayPark.before?.asset_entries || []).length > 0;
                  const hasLegacy = !!todayPark.before?.asset_id;
                  const isBroken = todayPark.before && !hasEntries && !hasLegacy;
                  const allowed = todayPark.after && (left > 0 || isBroken);
                  if (!allowed) return null;
                  return (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-[11px] text-rose-700 hover:bg-rose-50"
                      title={isBroken
                        ? "Legacy record — reset allowed"
                        : `Reset window closes in ${Math.floor(left / 60)}m ${left % 60}s`}
                      onClick={async () => {
                        if (!window.confirm("WARNING — this will permanently delete all After-cleaning data (length cleaned, photos, waste-removed answer, remarks, custom answers) for today. Do you want to proceed?")) return;
                        try {
                          await api.delete(`/pmc/cleaning-checklists/after?park_id=${parkId}`);
                          clearDraft(afterDraftKey);
                          toast.success("After-cleaning reset — please resubmit");
                          window.location.reload();
                        } catch (err) {
                          const detail = err?.response?.data?.detail;
                          toast.error(typeof detail === "string" ? detail : "Reset failed");
                        }
                      }}
                      data-testid="pmc-clean-reset-after"
                    >
                      {isBroken ? "Reset After" : `Reset After · ${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`}
                    </Button>
                  );
                })()}
              </div>
            </div>
            {afterDone ? (
              <div className="mt-1 text-xs text-emerald-700" data-testid="pmc-clean-after-done">
                Submitted. {isOsrAsset ? "Extent" : "Length"} cleaned: <span className="font-medium">{todayPark.after?.length_km_cleaned ?? "—"} {sizeUnitSuffix}</span>.
                {todayPark.after?.completed_as_per_plan != null ? (
                  <span className="ml-2">
                    As per plan: <span className="font-medium">{todayPark.after.completed_as_per_plan ? "Yes" : "No"}</span>
                    {todayPark.after.completed_as_per_plan === false && todayPark.after.not_completed_as_per_plan_reason
                      ? <span className="ml-1 text-rose-700">({todayPark.after.not_completed_as_per_plan_reason})</span>
                      : null}
                  </span>
                ) : null}
                {todayPark.after?.waste_removed != null ? (
                  <span className="ml-2">
                    Waste removed: <span className="font-medium">{todayPark.after.waste_removed ? "Yes" : "No"}</span>
                    {todayPark.after.waste_removed === false && todayPark.after.waste_not_removed_reason
                      ? <span className="ml-1 text-rose-700">({todayPark.after.waste_not_removed_reason})</span>
                      : null}
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="mt-1 text-xs text-emerald-700">
                Before submitted &middot; asset <span className="font-medium">{todayPark.before?.asset_name || "—"}</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <fieldset disabled={afterDone} className="contents">
              {/* Compute multi-asset list (preferred) or fallback to legacy
                  single-asset row. */}
              {(() => null)()}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {!isBuiltinDisabled("after", "completion_mode") && (
                  <div>
                    <Label className="text-xs">Cleaning completed?</Label>
                    <Select value={completionMode || "_unset"} onValueChange={(v) => setCompletionMode(v === "_unset" ? "" : v)}>
                      <SelectTrigger data-testid="pmc-clean-completion-mode"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_unset">— Not applicable —</SelectItem>
                        <SelectItem value="single_stretch">Single stretch</SelectItem>
                        <SelectItem value="both_side">Both sides</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {!isBuiltinDisabled("after", "length_km_cleaned") && (() => {
                  const beforeEntries = todayPark.before?.asset_entries || [];
                  if (beforeEntries.length > 1) {
                    // Multi-asset path: render a small per-asset list
                    return (
                      <div className="space-y-1.5" data-testid="pmc-clean-per-asset-lengths">
                        <Label className="text-xs">Approx. size cleaned (per asset) *</Label>
                        <div className="flex flex-col gap-1.5 rounded-md border bg-slate-50/60 p-2">
                          {beforeEntries.map((e) => {
                            const t = (e.asset_type || "").toLowerCase();
                            const unit = t === "osr" ? "acres" : "km";
                            const val = afterPerAssetLengths[e.asset_id] ?? "";
                            const isRoad = t === "road";
                            const num = Number(val);
                            const missing = isRoad && (val === "" || !Number.isFinite(num) || num <= 0);
                            return (
                              <div key={e.asset_id} className="flex items-center gap-2 text-[12px]">
                                <span className="min-w-0 flex-1 truncate">
                                  <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">{t}</span>
                                  {e.asset_name}
                                </span>
                                <Input
                                  value={val}
                                  onChange={(ev) => setAfterPerAssetLengths((m) => ({ ...m, [e.asset_id]: ev.target.value }))}
                                  placeholder={`0.00 ${unit}`}
                                  inputMode="decimal"
                                  className={`h-8 w-[120px] ${missing && !afterDone ? "border-rose-400 focus-visible:ring-rose-400" : ""}`}
                                  data-testid={`pmc-clean-len-${e.asset_id}`}
                                />
                                <span className="w-[40px] text-[10px] text-muted-foreground">{unit}</span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          Each road asset must have a value &gt; 0. OSR / others are optional but recommended.
                        </p>
                      </div>
                    );
                  }
                  // Legacy single-asset path
                  return (
                    <div>
                      <Label className="text-xs">
                        {sizeUnitLabel}
                        {isRoadAsset ? <span className="ml-1 text-rose-600">*</span> : null}
                      </Label>
                      <Input
                        value={lengthCleaned}
                        onChange={(e) => setLengthCleaned(e.target.value)}
                        placeholder={sizeUnitPlaceholder}
                        inputMode="decimal"
                        className={lengthRequiredMissing && !afterDone ? "border-rose-400 focus-visible:ring-rose-400" : ""}
                        aria-invalid={lengthRequiredMissing && !afterDone ? true : undefined}
                        data-testid="pmc-clean-length-km"
                      />
                      {isRoadAsset ? (
                        <p className={`mt-1 text-[11px] ${lengthRequiredMissing && !afterDone ? "text-rose-600" : "text-muted-foreground"}`} data-testid="pmc-clean-length-km-hint">
                          Required for road assets — admins use this to track per-km cycle progress.
                        </p>
                      ) : isOsrAsset ? (
                        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="pmc-clean-extent-acres-hint">
                          For OSR plots, record the approximate area cleaned today in acres.
                        </p>
                      ) : null}
                    </div>
                  );
                })()}
              </div>

              {!isBuiltinDisabled("after", "photos") && (() => {
                const beforeEntries = todayPark.before?.asset_entries || [];
                const isRemoteFiling = visitMode === "remote";
                if (isRemoteFiling) {
                  return (
                    <CapturePanel
                      label="After-cleaning evidence photos"
                      helper="1 to 4 photos (gallery upload)"
                      photos={afterPhotos}
                      onRemove={(i) => setAfterPhotos((p) => p.filter((_, idx) => idx !== i))}
                      onZoom={(s) => setLightboxSrc(s)}
                      galleryUpload
                      onGalleryFiles={handleAfterGalleryUpload}
                      maxPhotos={4}
                      testidPrefix="pmc-clean-after-gallery"
                    />
                  );
                }
                const entriesToDisplay = beforeEntries.length > 0
                  ? beforeEntries
                  : (todayPark.before?.asset_id
                      ? [{ asset_id: todayPark.before.asset_id, asset_type: todayPark.before.asset_type, asset_name: todayPark.before.asset_name }]
                      : []);
                if (entriesToDisplay.length > 0) {
                  return (
                    <div className="space-y-3" data-testid="pmc-clean-after-multi">
                      <div className="text-xs font-medium text-slate-600">
                        After-cleaning photos (per area type / asset, 1–4 each){useGalleryForPhotos ? " — gallery upload" : ""}
                      </div>
                      {entriesToDisplay.map((e) => {
                        const ownPhotos = afterPhotos.filter((p) => p.asset_id === e.asset_id);
                        const areaLabel = ASSET_TYPE_LABEL[e.asset_type] || e.asset_type || "Area";
                        return (
                          <div key={e.asset_id || e.asset_name} className="rounded-md border bg-slate-50/40 p-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <div className="text-sm font-medium text-slate-700" data-testid={`pmc-clean-after-asset-${e.asset_id}`}>
                                {areaLabel} – After Cleaning ({e.asset_name || "Location"})
                              </div>
                              <Badge variant="secondary" className="text-[10px]">{areaLabel}</Badge>
                            </div>
                            <CapturePanel
                              label={`After-cleaning photos (${areaLabel})`}
                              helper={useGalleryForPhotos ? "1 to 4 photos (gallery upload)" : "1 to 4 photos (live camera)"}
                              photos={ownPhotos}
                              onRemove={(localIdx) => {
                                const target = ownPhotos[localIdx];
                                setAfterPhotos((all) => all.filter((p) => p !== target));
                              }}
                              onZoom={(s) => setLightboxSrc(s)}
                              galleryUpload={useGalleryForPhotos}
                              onGalleryFiles={(ev) => { setCurrentAfterAssetId(e.asset_id); handleAfterGalleryUpload(ev, e.asset_id); }}
                              onOpenCamera={() => { setCurrentAfterAssetId(e.asset_id); setShowAfterCamera(true); }}
                              maxPhotos={4}
                              testidPrefix={`pmc-clean-after-${e.asset_id}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                return (
                  <CapturePanel
                    label="After-cleaning photos"
                    helper={useGalleryForPhotos ? "1 to 4 photos (gallery upload)" : "1 to 4 photos (live camera)"}
                    photos={afterPhotos}
                    onRemove={(i) => setAfterPhotos((p) => p.filter((_, idx) => idx !== i))}
                    onZoom={(s) => setLightboxSrc(s)}
                    galleryUpload={useGalleryForPhotos}
                    onGalleryFiles={handleAfterGalleryUpload}
                    onOpenCamera={() => setShowAfterCamera(true)}
                    maxPhotos={4}
                    testidPrefix="pmc-clean-after"
                  />
                );
              })()}

              <div>
                {!isBuiltinDisabled("after", "completed_as_per_plan") && (
                  <>
                    <Label className="text-xs">Cleaning activity completed as per plan? *</Label>
                    <div className="mt-1"><YesNoToggle value={asPerPlan} onChange={setAsPerPlan} testid="pmc-clean-per-plan" /></div>
                    {asPerPlan === false ? (
                      <Textarea
                        rows={2}
                        placeholder="Reason — e.g., rain delay, worker shortage, equipment issue, emergency assignment…"
                        value={notCompletedAsPerPlanReason}
                        onChange={(e) => setNotCompletedAsPerPlanReason(e.target.value)}
                        className="mt-2"
                        data-testid="pmc-clean-not-per-plan-reason"
                      />
                    ) : null}
                  </>
                )}
              </div>

              {!isBuiltinDisabled("after", "waste_removed") && (
                <div>
                  <Label className="text-xs">Were the cleaned wastes removed from the industrial park today? *</Label>
                  <div className="mt-1">
                    <YesNoToggle value={wasteRemoved} onChange={setWasteRemoved} testid="pmc-clean-waste-removed" />
                  </div>
                  {wasteRemoved === false ? (
                    <Textarea
                      rows={2}
                      placeholder="Reason — e.g., truck unavailable, dump site closed, will be lifted tomorrow…"
                      value={wasteNotRemovedReason}
                      onChange={(e) => setWasteNotRemovedReason(e.target.value)}
                      className="mt-2"
                      data-testid="pmc-clean-waste-reason"
                    />
                  ) : null}
                </div>
              )}

              <CustomQuestionsRenderer
                kind="after"
                questions={customQuestions.after}
                answers={customAnswers.after}
                setAnswer={(qid, patch) => setCustomAnswer("after", qid, patch)}
              />

              <Textarea
                placeholder="Remarks (optional)…"
                rows={2}
                value={afterRemarks}
                onChange={(e) => setAfterRemarks(e.target.value)}
                data-testid="pmc-clean-after-remarks"
              />

              <Button
                onClick={submitAfter}
                disabled={submittingAfter || afterDone || lengthRequiredMissing}
                size="lg"
                className="h-12 text-base"
                data-testid="pmc-clean-after-submit"
              >
                {afterDone ? "Submitted ✓" : submittingAfter ? "Submitting…" : (lengthRequiredMissing ? "Enter km cleaned to submit" : "Submit After-cleaning")}
              </Button>
            </fieldset>
          </CardContent>
        </Card>
      )}

      {/* Camera modal — opens fullscreen-ish on mobile so the user doesn't
          need to scroll. Auto-closes once a photo is captured. */}
      <Dialog open={showBeforeCamera} onOpenChange={(v) => !v && setShowBeforeCamera(false)}>
        <DialogContent className="max-w-md p-3 sm:p-4" data-testid="pmc-clean-before-cam-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Before-cleaning photo</DialogTitle>
          </DialogHeader>
          <CameraCapture
            onCapture={(b64) => handleBeforeCapture(b64, currentBeforeAssetId)}
            overlayDateTime
            testIdPrefix="pmc-clean-before-cam"
          />
          <Button variant="outline" size="sm" className="mt-1 w-full" onClick={() => setShowBeforeCamera(false)}>
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showAfterCamera} onOpenChange={(v) => !v && setShowAfterCamera(false)}>
        <DialogContent className="max-w-md p-3 sm:p-4" data-testid="pmc-clean-after-cam-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">After-cleaning photo</DialogTitle>
          </DialogHeader>
          <CameraCapture
            onCapture={(b64) => handleAfterCapture(b64, currentAfterAssetId)}
            overlayDateTime
            testIdPrefix="pmc-clean-after-cam"
          />
          <Button variant="outline" size="sm" className="mt-1 w-full" onClick={() => setShowAfterCamera(false)}>
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showWorkerCamera} onOpenChange={(v) => !v && setShowWorkerCamera(false)}>
        <DialogContent className="max-w-md p-3 sm:p-4" data-testid="pmc-clean-workers-cam-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Workers at work photo</DialogTitle>
          </DialogHeader>
          <CameraCapture
            onCapture={handleWorkerCapture}
            overlayDateTime
            testIdPrefix="pmc-clean-workers-cam"
          />
          <Button variant="outline" size="sm" className="mt-1 w-full" onClick={() => setShowWorkerCamera(false)}>
            Cancel
          </Button>
        </DialogContent>
      </Dialog>

      {lightboxSrc ? <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} /> : null}



      {/* Dialog to Add New Work Location Request (saved as Pending Approval until Admin approves) */}
      <Dialog open={addLocationOpen} onOpenChange={(v) => { if (!v && !submittingAddLocation) setAddLocationOpen(false); }}>
        <DialogContent className="sm:max-w-md p-4 max-h-[90vh] overflow-y-auto" data-testid="pmc-clean-add-location-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <PlusCircle className="h-5 w-5 text-emerald-600" />
              Add New Work Location Request
            </DialogTitle>
            <div className="text-xs text-muted-foreground">
              Workers moving to a new location? Select the work location and provide mandatory business remarks for Admin approval.
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-4 py-2">
            <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900 flex items-start gap-2">
              <Clock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <b>Approval Required:</b> Requests are saved as <b>Pending Approval</b>. You can proceed with photo capture and questionnaires once Admin approves this request.
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Area type (filter)</Label>
                <Select value={addAssetType} onValueChange={(v) => { setAddAssetType(v); setAddAssetId(""); }}>
                  <SelectTrigger data-testid="add-loc-asset-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(ASSET_TYPE_LABEL).map((k) => (
                      <SelectItem key={k} value={k}>{ASSET_TYPE_LABEL[k]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Select asset / work location *</Label>
                <Select
                  value={addAssetId || "_picker"}
                  onValueChange={(v) => setAddAssetId(v === "_picker" || v === "_none" ? "" : v)}
                >
                  <SelectTrigger data-testid="add-loc-asset-id">
                    <SelectValue placeholder={availableAddAssets.length === 0 ? "No available assets" : "Pick an asset to add"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_picker" disabled>+ Pick an asset to add</SelectItem>
                    {availableAddAssets.length === 0 ? (
                      <SelectItem value="_none" disabled>— All assets of this type already added —</SelectItem>
                    ) : availableAddAssets.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                        {a.length_km != null
                          ? ` (${a.length_km} ${a.asset_type === "osr" ? "acres" : "km"})`
                          : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-foreground flex items-center justify-between">
                <span>Remarks / Business Justification *</span>
                <span className="text-[10px] font-normal text-rose-600">Mandatory</span>
              </Label>
              <Textarea
                value={addLocationRemarks}
                onChange={(e) => setAddLocationRemarks(e.target.value)}
                placeholder="Enter business justification / reason for adding this work location..."
                rows={3}
                className="mt-1 text-sm"
                data-testid="add-loc-remarks"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddLocationOpen(false)} disabled={submittingAddLocation}>
              Cancel
            </Button>
            <Button
              onClick={submitAddLocation}
              disabled={submittingAddLocation || !addAssetId || !addLocationRemarks.trim()}
              data-testid="add-loc-submit"
            >
              {submittingAddLocation ? "Submitting…" : "Submit Request (Pending Approval)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog to Request Admin Approval to Reset Before-Cleaning (when 10-min timer expired) */}
      <Dialog open={resetBeforeOpen} onOpenChange={(v) => { if (!v && !submittingResetBefore) setResetBeforeOpen(false); }}>
        <DialogContent className="sm:max-w-md p-4" data-testid="pmc-clean-reset-before-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Lock className="h-5 w-5 text-amber-600" />
              Request Approval to Reset Before-Cleaning
            </DialogTitle>
            <div className="text-xs text-muted-foreground mt-1">
              The 10-minute edit/reset window for Before-cleaning has expired. Submit mandatory remarks for Admin approval to access and reset the Before-cleaning checklist.
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-3 py-2">
            <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900 flex items-start gap-2">
              <Clock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <b>Approval Required:</b> Your request will be submitted to Admin as <b>Pending Approval</b>. Once Admin approves, your Before-cleaning checklist will be reset so you can resubmit.
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-foreground flex items-center justify-between mb-1">
                <span>Remarks / Business Justification *</span>
                <span className="text-[10px] font-normal text-rose-600">Mandatory</span>
              </Label>
              <Textarea
                value={resetBeforeRemarks}
                onChange={(e) => setResetBeforeRemarks(e.target.value)}
                placeholder="Enter mandatory remarks / business reason for Admin approval..."
                rows={3}
                className="text-sm"
                data-testid="pmc-clean-reset-before-remarks"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setResetBeforeOpen(false)} disabled={submittingResetBefore}>
              Cancel
            </Button>
            <Button
              onClick={submitResetBeforeRequest}
              disabled={submittingResetBefore || !resetBeforeRemarks.trim()}
              data-testid="pmc-clean-reset-before-submit"
            >
              {submittingResetBefore ? "Submitting…" : "Submit Request for Admin Approval"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

/**
 * CapturePanel — unified capture UI for both Before and After.
 *  - Live camera mode (`onOpenCamera`): big Capture button.
 *  - Gallery mode (`galleryUpload + onGalleryFiles`): Upload button.
 * Thumbnails are rendered immediately under the action button so the user
 * doesn't have to scroll back up to see what's been captured.
 */
function CapturePanel({
  label, helper, photos, onRemove, onZoom,
  onOpenCamera, galleryUpload, onGalleryFiles,
  maxPhotos, testidPrefix,
}) {
  const count = photos?.length || 0;
  const atMax = count >= maxPhotos;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Label className="text-sm font-medium">{label}</Label>
        <span className="text-[11px] text-muted-foreground">{helper} &middot; {count}/{maxPhotos}</span>
      </div>
      {galleryUpload ? (
        <label className="block">
          <Button
            type="button"
            asChild
            className="h-12 w-full text-base"
            disabled={atMax}
            data-testid={`${testidPrefix}-button`}
          >
            <span><Upload className="mr-2 h-5 w-5" /> {atMax ? "Maximum reached" : "Upload photo"}</span>
          </Button>
          <input
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={onGalleryFiles}
            disabled={atMax}
            data-testid={`${testidPrefix}-input`}
          />
        </label>
      ) : (
        <Button
          type="button"
          onClick={onOpenCamera}
          disabled={atMax}
          className="h-12 w-full text-base"
          data-testid={`${testidPrefix}-camera`}
        >
          <Camera className="mr-2 h-5 w-5" /> {atMax ? "Maximum reached" : "Capture photo"}
        </Button>
      )}
      {count > 0 ? (
        <PhotoStrip photos={photos} onRemove={onRemove} onZoom={onZoom} />
      ) : (
        <div className="rounded-md border border-dashed bg-slate-50 px-3 py-4 text-center text-[11px] text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
          <ImagePlus className="mx-auto mb-1 h-4 w-4" /> No photos yet
        </div>
      )}
    </div>
  );
}
